import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiShield, FiLock } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const RolesList = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/role/list`, { headers: { Authorization: `Bearer ${token}` } });
      const result = await res.json();
      if (result.status) setRoles(result.data || []);
      else toast.error(result.message || "Can't fetch roles");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const removeRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      const res = await fetch(`${baseUrl}/role/remove/${role.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      const result = await res.json();
      if (!result.status) throw new Error(result.message || "Delete failed");
      toast.success("Role deleted");
      fetchRoles();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <FiShield size={20} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Roles</h2>
                <p className="text-sm text-gray-400 mt-0.5">Reusable permission templates assigned to staff</p>
              </div>
            </div>
            {hasPermission("roles", "create") && (
              <button onClick={() => navigate("/roles/create")}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition">
                <FiPlus size={16} /> Add Role
              </button>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : roles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiShield size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No roles yet. Add one above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Description</th>
                      <th className="px-5 py-3 text-center">Permissions</th>
                      <th className="px-5 py-3 text-center">Staff</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {roles.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-3.5 font-medium text-gray-800">
                          <div className="flex items-center gap-2">
                            {role.name}
                            {role.isSystem === 1 || role.isSystem === true ? (
                              <span title="Default system role" className="text-gray-300"><FiLock size={12} /></span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-gray-500">{role.description || "—"}</td>
                        <td className="px-5 py-3.5 text-center text-gray-600">{role.permissionCount}</td>
                        <td className="px-5 py-3.5 text-center text-gray-600">{role.staffCount}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1.5">
                            {hasPermission("roles", "edit") && (
                              <button onClick={() => navigate(`/roles/${role.id}/edit`)} title="Edit" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-amber-600">
                                <FiEdit2 size={14} />
                              </button>
                            )}
                            {hasPermission("roles", "delete") && !role.isSystem && role.staffCount === 0 && (
                              <button onClick={() => removeRole(role)} title="Delete" className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600">
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesList;
